/**
 * GenerateRangeVideoDialog
 * Single popup that lets the user pick a time range and the video settings,
 * then creates a video from the photos that fall in that range.
 *
 * Range options:
 *   - Last N hours/days/weeks/months
 *   - All  (whole view, equivalent to first → last photo)
 *   - Custom (date range picker)
 */

import { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Radio,
  InputNumber,
  Select,
  DatePicker,
  Space,
  Typography,
  Button,
  Divider,
  Tag,
  Alert,
} from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { MaterialIcon } from "@/components/MaterialIcon";
import { MODAL_BLUR_STYLES } from "@/styles/modalStyles";
import type { Photo, VideoResolutionLimit } from "@/types";
import type { VideoConfig } from "@/components/CreateVideoDialog";

const { Text } = Typography;
const { RangePicker } = DatePicker;

type RangeMode = "last" | "all" | "selected" | "custom";
type Unit = "hours" | "days" | "weeks" | "months";
type Resolution = "1080p" | "4k";
type FPS = 15 | 24 | 25 | 30 | 60;
type AspectRatio = "16:9" | "9:16" | "4:3" | "3:4" | "1:1";

const UNIT_OPTIONS: { value: Unit; label: string }[] = [
  { value: "hours", label: "hours" },
  { value: "days", label: "days" },
  { value: "weeks", label: "weeks" },
  { value: "months", label: "months" },
];

const FPS_OPTIONS: FPS[] = [15, 24, 25, 30, 60];
const ASPECT_RATIO_OPTIONS: AspectRatio[] = ["16:9", "9:16", "4:3", "3:4", "1:1"];

interface GenerateRangeVideoDialogProps {
  open: boolean;
  photos: Photo[]; // All photos for the view (any order)
  selectedPhotos?: Photo[];
  hasActiveJob: boolean;
  maxResolution?: VideoResolutionLimit;
  onCreateVideo: (config: VideoConfig) => Promise<void>;
  onCancel: () => void;
}

function unitToSeconds(n: number, unit: Unit): number {
  switch (unit) {
    case "hours":
      return n * 3600;
    case "days":
      return n * 86400;
    case "weeks":
      return n * 7 * 86400;
    case "months":
      return n * 30 * 86400;
  }
}

export function GenerateRangeVideoDialog({
  open,
  photos,
  selectedPhotos = [],
  hasActiveJob,
  maxResolution = "1080p",
  onCreateVideo,
  onCancel,
}: GenerateRangeVideoDialogProps) {
  const [mode, setMode] = useState<RangeMode>("last");
  const [lastN, setLastN] = useState<number>(1);
  const [unit, setUnit] = useState<Unit>("days");
  const [customRange, setCustomRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [resolution, setResolution] = useState<Resolution>("1080p");
  const [fps, setFps] = useState<FPS>(24);
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>("16:9");
  const [isCreating, setIsCreating] = useState(false);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      // Default to "selected" when the user opened the dialog with photos
      // already selected in the gallery, otherwise fall back to "last".
      setMode(selectedPhotos.length > 0 ? "selected" : "last");
      setLastN(1);
      setUnit("days");
      setCustomRange(null);
      setResolution(maxResolution === "4k" ? "1080p" : "1080p");
      setFps(24);
      setAspectRatio("16:9");
      setIsCreating(false);
    }
  }, [open, maxResolution, selectedPhotos.length]);

  // Set of days (YYYY-MM-DD) that contain at least one photo, used to grey
  // out empty days in the custom range picker.
  const daysWithPhotos = useMemo<Set<string>>(() => {
    const set = new Set<string>();
    for (const p of photos) {
      const t = p.capturedAt?.toMillis?.() ?? 0;
      if (t === 0) continue;
      set.add(dayjs(t).format("YYYY-MM-DD"));
    }
    return set;
  }, [photos]);

  // Compute the [startMs, endMs] window from the current selection.
  const range = useMemo<{ startMs: number; endMs: number } | null>(() => {
    if (mode === "all") {
      if (photos.length === 0) return null;
      let min = Number.POSITIVE_INFINITY;
      let max = 0;
      for (const p of photos) {
        const t = p.capturedAt?.toMillis?.() ?? 0;
        if (t === 0) continue;
        if (t < min) min = t;
        if (t > max) max = t;
      }
      if (!isFinite(min) || max === 0) return null;
      return { startMs: min, endMs: max };
    }
    if (mode === "selected") {
      if (selectedPhotos.length === 0) return null;
      let min = Number.POSITIVE_INFINITY;
      let max = 0;
      for (const p of selectedPhotos) {
        const t = p.capturedAt?.toMillis?.() ?? 0;
        if (t === 0) continue;
        if (t < min) min = t;
        if (t > max) max = t;
      }
      if (!isFinite(min) || max === 0) return null;
      return { startMs: min, endMs: max };
    }
    if (mode === "last") {
      const n = Math.max(1, Math.floor(lastN || 1));
      const endMs = Date.now();
      return { startMs: endMs - unitToSeconds(n, unit) * 1000, endMs };
    }
    // custom
    if (!customRange || !customRange[0] || !customRange[1]) return null;
    return {
      startMs: customRange[0].startOf("day").valueOf(),
      endMs: customRange[1].endOf("day").valueOf(),
    };
  }, [mode, lastN, unit, customRange, photos, selectedPhotos]);

  // Filter photos by range and sort chronologically.
  const photosInRange = useMemo<Photo[]>(() => {
    if (mode === "selected") {
      return [...selectedPhotos].sort((a, b) => {
        const at = a.capturedAt?.toMillis?.() ?? 0;
        const bt = b.capturedAt?.toMillis?.() ?? 0;
        return at - bt;
      });
    }
    if (!range) return [];
    const list = photos.filter((p) => {
      const t = p.capturedAt?.toMillis?.() ?? 0;
      return t >= range.startMs && t <= range.endMs;
    });
    return list.sort((a, b) => {
      const at = a.capturedAt?.toMillis?.() ?? 0;
      const bt = b.capturedAt?.toMillis?.() ?? 0;
      return at - bt;
    });
  }, [photos, selectedPhotos, range, mode]);

  const photoCount = photosInRange.length;

  const handleOk = async () => {
    if (!range || photosInRange.length === 0) return;
    setIsCreating(true);
    try {
      const startDate = new Date(range.startMs).toISOString().split("T")[0];
      const endDate = new Date(range.endMs).toISOString().split("T")[0];
      await onCreateVideo({
        resolution,
        fps,
        aspectRatio,
        photoIds: photosInRange.map((p) => p.photoId),
        photoCount,
        dateRange: { start: startDate, end: endDate },
      });
    } finally {
      setIsCreating(false);
    }
  };

  const canCreate = !hasActiveJob && photoCount > 0 && !isCreating;

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      title={
        <Space>
          <MaterialIcon name="movie_filter" size={20} style={{ color: "#1677ff" }} />
          <span>Generate video</span>
        </Space>
      }
      width={520}
      destroyOnClose
      styles={MODAL_BLUR_STYLES}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="ok"
          type="primary"
          loading={isCreating}
          disabled={!canCreate}
          onClick={handleOk}
        >
          OK
        </Button>,
      ]}
    >
      <Space direction="vertical" size={20} style={{ width: "100%" }}>
        {hasActiveJob && (
          <Alert
            type="warning"
            showIcon
            message="You already have a video being generated. Wait for it to finish."
          />
        )}

        {/* Time range selector */}
        <div>
          <Text strong style={{ display: "block", marginBottom: 10 }}>
            Time range
          </Text>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Radio
                checked={mode === "last"}
                onChange={() => setMode("last")}
              >
                Last
              </Radio>
              <InputNumber
                min={1}
                max={999}
                value={lastN}
                onChange={(v) => {
                  setMode("last");
                  setLastN(typeof v === "number" ? v : 1);
                }}
                onFocus={() => setMode("last")}
                style={{ width: 72 }}
              />
              <Select<Unit>
                value={unit}
                onChange={(v) => {
                  setMode("last");
                  setUnit(v);
                }}
                onFocus={() => setMode("last")}
                style={{ width: 110 }}
                options={UNIT_OPTIONS}
              />
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Radio
                checked={mode === "all"}
                onChange={() => setMode("all")}
              >
                All photos
              </Radio>
              <Text type="secondary" style={{ fontSize: 12 }}>
                (every photo in this view)
              </Text>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Radio
                checked={mode === "selected"}
                onChange={() => setMode("selected")}
                disabled={selectedPhotos.length === 0}
              >
                Selected photos
              </Radio>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {selectedPhotos.length > 0
                  ? `(${selectedPhotos.length} selected)`
                  : "(use shift+click to select interval)"}
              </Text>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Radio
                checked={mode === "custom"}
                onChange={() => setMode("custom")}
              >
                Custom
              </Radio>
              <RangePicker
                value={customRange ?? undefined}
                onChange={(v) => {
                  setMode("custom");
                  setCustomRange((v as [Dayjs | null, Dayjs | null]) ?? null);
                }}
                onFocus={() => setMode("custom")}
                format="YYYY-MM-DD"
                allowClear
                disabledDate={(d) => !daysWithPhotos.has(d.format("YYYY-MM-DD"))}
              />
            </div>
          </div>
          <div style={{ marginTop: 10 }}>
            <Tag color={photoCount > 0 ? "blue" : "default"}>
              {photoCount.toLocaleString()} photos in range
            </Tag>
            {range && (
              <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                {dayjs(range.startMs).format("YYYY-MM-DD HH:mm")} →{" "}
                {dayjs(range.endMs).format("YYYY-MM-DD HH:mm")}
              </Text>
            )}
          </div>
        </div>

        <Divider style={{ margin: "0" }} />

        {/* Video settings */}
        <div>
          <Text strong style={{ display: "block", marginBottom: 10 }}>
            Video settings
          </Text>
          <Space direction="vertical" size={12} style={{ width: "100%" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Select<Resolution>
                value={resolution}
                onChange={setResolution}
                options={[
                  { value: "1080p", label: "1080p" },
                  {
                    value: "4k",
                    // Naming the tier is the whole point: a silently greyed-out
                    // option reads as a bug, not as a plan limit.
                    label: maxResolution !== "4k" ? "4K — Pro" : "4K",
                    disabled: maxResolution !== "4k",
                  },
                ]}
              />
              <Select<FPS>
                value={fps}
                onChange={setFps}
                options={FPS_OPTIONS.map((f) => ({ value: f, label: `${f} fps` }))}
              />
              <Select<AspectRatio>
                value={aspectRatio}
                onChange={setAspectRatio}
                options={ASPECT_RATIO_OPTIONS.map((r) => ({ value: r, label: r }))}
              />
            </div>
          </Space>
        </div>
      </Space>
    </Modal>
  );
}
